// CoachText helpers. ARCHITECTURE.md §5 (CoachText slots); ENGINE_DESIGN.md §8.2 (templates are the
// mandatory fallback when the LLM call fails or the post-check rejects twice). The key helper is
// templateFallbackCoachText: it fills the four slots DETERMINISTICALLY from EngineFacts so the coach
// drawer is always populated - $0, 0ms, no model - even with the Worker or Anthropic down.
import { CoachTextSchema, } from '@postflop/schema';
import { BOARD_EVENT_PHRASE } from './cards';
import { ruleFired } from './templates/ruleFired';
/** An all-empty CoachText - the per-slot abstain baseline (every slot unsupported). */
export function emptyCoachText() {
    return { beginner_thought: '', pro_thought: '', what_changed: '', villain_read: '' };
}
/** Validate an untrusted CoachText (e.g. a model or KV payload) against the schema; throws on drift. */
export function parseCoachText(value) {
    return CoachTextSchema.parse(value);
}
/** True when every slot is empty/whitespace - the model (or fallback) had nothing to say. */
export function isEmptyCoachText(t) {
    return (!t.beginner_thought.trim() &&
        !t.pro_thought.trim() &&
        !(t.what_changed ?? '').trim() &&
        !t.villain_read.trim());
}
const HAND_CLASS_PROSE = (facts) => facts.hero.handClass.replace(/_/g, ' ');
const DELTA_VERB = {
    collapsed: 'collapsed',
    slipped: 'slipped',
    held: 'held',
    improved: 'improved',
    surged: 'surged',
};
/** A CARD-FREE what-changed summary for the CoachText slot. Unlike the whatChanged TEMPLATE (which
 *  renders the literal board card into a CoachLine - §8.2 allows it), the CoachText slot must stay
 *  card-free: it is what the LLM produces (never sees cards) and what gets KV-cached under the bucketed
 *  digest key, so a literal card here would be stale for any other hand that buckets the same. Built
 *  from the structured whatChanged block + the classified event phrase (both card-free). '' preflop. */
function cardFreeWhatChanged(facts) {
    if (facts.street === 'preflop')
        return '';
    const parts = [];
    const ev = facts.board.streetEvent;
    if (ev) {
        const extras = [];
        if (ev.completesFlush)
            extras.push('completing a flush');
        if (ev.pairsBoard)
            extras.push('pairing the board');
        const suffix = extras.length ? ` (${extras.join(', ')})` : '';
        parts.push(`The ${facts.street} brought ${BOARD_EVENT_PHRASE[ev.kind]}${suffix}.`);
    }
    const bucket = facts.hero.equityDeltaBucket ?? facts.whatChanged?.equityDeltaBucket;
    if (bucket)
        parts.push(`Your equity ${DELTA_VERB[bucket]}.`);
    const shift = facts.whatChanged?.rangeAdvantageShift;
    if (shift === 'toHero')
        parts.push('Range advantage moved toward you.');
    else if (shift === 'toVillain')
        parts.push('Range advantage moved toward the villain.');
    return parts.join(' ');
}
/**
 * The mandatory LLM-free fallback (ENGINE_DESIGN.md §8.2). Fills the four CoachText slots from
 * EngineFacts using the deterministic templates - never a model:
 *   - beginner_thought: a plain read of hand strength + the recommended action.
 *   - pro_thought:      the fired-rule principle(s) (or the baseline note when none fired).
 *   - what_changed:     the what-changed lines (empty preflop - the slot is post-preflop only).
 *   - villain_read:     the villain archetype + range shape in one factual sentence.
 * Card-free by construction (it references handClass/archetype/buckets, not cards), so it is safe as
 * both the coach drawer text AND a KV-cacheable answer.
 */
export function templateFallbackCoachText(facts) {
    const rec = facts.recommendation;
    const beginner_thought = `You have ${HAND_CLASS_PROSE(facts)}. The sound play here is to ${rec.action}.`;
    const rules = ruleFired(facts).filter((l) => l.kind === 'rule');
    const pro_thought = rules.length
        ? rules.map((l) => l.text).join(' ')
        : `This is the standard low-stakes line: ${rec.baselineAction}.`;
    const wc = cardFreeWhatChanged(facts);
    const rs = facts.villain.rangeShape;
    const top = rs.topClasses.length ? `, mostly ${rs.topClasses.join(', ')}` : '';
    const villain_read = `He plays like a ${facts.villain.archetypeId}: value-heavy ${Math.round(rs.valueHeavyPct)}%, bluffs ${Math.round(rs.bluffPct)}%${top}.`;
    return CoachTextSchema.parse({
        beginner_thought,
        pro_thought,
        what_changed: wc, // '' preflop - schema allows the slot optional/empty
        villain_read,
    });
}
/** A card-free what-changed summary built from the bucketed FactsDigest (not full EngineFacts). Mirrors
 *  cardFreeWhatChanged but sources the classified event + equity-delta bucket the digest carries. The
 *  digest has no whatChanged.rangeAdvantageShift block, so that line is omitted here (present in the full
 *  EngineFacts fallback). '' preflop. */
function digestCardFreeWhatChanged(digest) {
    if (digest.street === 'preflop')
        return '';
    const parts = [];
    const ev = digest.board.streetEvent;
    if (ev) {
        const extras = [];
        if (ev.completesFlush)
            extras.push('completing a flush');
        if (ev.pairsBoard)
            extras.push('pairing the board');
        const suffix = extras.length ? ` (${extras.join(', ')})` : '';
        parts.push(`The ${digest.street} brought ${BOARD_EVENT_PHRASE[ev.kind]}${suffix}.`);
    }
    const bucket = digest.hero.equityDeltaBucket;
    if (bucket)
        parts.push(`Your equity ${DELTA_VERB[bucket]}.`);
    return parts.join(' ');
}
/**
 * The Worker-side deterministic fallback (ARCHITECTURE.md §8.2 fallback, §5 firewall). /api/coach receives
 * ONLY the bucketed FactsDigest (never EngineFacts - no cards cross the boundary), so when the LLM call
 * fails or the post-check rejects twice, the Worker fills the four CoachText slots from the digest alone:
 *   - beginner_thought: hand strength + the recommended action.
 *   - pro_thought:      the fired-rule principle strings, or a baseline note when none fired (the digest
 *                       carries no baselineAction, so the baseline line is generic).
 *   - what_changed:     the card-free event/delta summary (empty preflop).
 *   - villain_read:     the villain archetype + range shape.
 * Card-free by construction (handClass/archetype/buckets only), so it is safe both as drawer text AND as a
 * KV-cacheable answer under the bucketed key. Never contradicts the engine - it names `recommendation.action`
 * verbatim - so it always passes the post-check.
 */
export function digestFallbackCoachText(digest) {
    const rec = digest.recommendation;
    const beginner_thought = `You have ${digest.hero.handClass.replace(/_/g, ' ')}. The sound play here is to ${rec.action}.`;
    const pro_thought = rec.firedRules.length
        ? rec.firedRules.map((r) => r.principle).join(' ')
        : `This is the standard line here: ${rec.action}.`;
    const wc = digestCardFreeWhatChanged(digest);
    const rs = digest.villain.rangeShape;
    const top = rs.topClasses.length ? `, mostly ${rs.topClasses.join(', ')}` : '';
    const villain_read = `He plays like a ${digest.villain.archetypeId}: value-heavy ${Math.round(rs.valueHeavyPct)}%, bluffs ${Math.round(rs.bluffPct)}%${top}.`;
    return CoachTextSchema.parse({
        beginner_thought,
        pro_thought,
        what_changed: wc,
        villain_read,
    });
}
//# sourceMappingURL=coachText.js.map